// Turns the row-level preview and paired-file review's teacher-related data
// into the same { severity, message } shape ImportPreview's year-over-year
// Findings already render -- so "N teacher names look new" and "the roster
// says a school moved 40% of a grade" read as one consistent list instead of
// two visually different UI languages bolted together.
//
// Pure and plain-object-in/plain-array-out on purpose: no React, no fetch,
// so it's testable without a component harness (see
// studentImportTeacherFindings.test.js) and reusable if a future screen
// needs the same synthesis.
const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

// StudentImportService::ClassMoves: children the file moves to a different
// teacher, and "classes" spanning PK to 8th. An import moves every matched child
// to the teacher the file names, so a file that lists a staff member or a period
// number instead of the teacher (John C. Fremont, 10/02/2026: 374 children) is
// only caught here, before it runs.
export const classMoveFindings = (classMoves) => {
  if (!classMoves) return [];
  if (classMoves.error) return [{ key: 'class-moves-error', severity: 'warn', message: classMoves.error }];

  const items = [];
  const {
    moved, compared, groups, alert,
  } = classMoves;
  if (moved > 0) {
    const pct = compared > 0 ? Math.round((moved * 100) / compared) : 0;
    const shown = (groups || []).map((g) => `${g.from} → ${g.to} (${g.count})`).join('; ');
    const advice = alert
      ? ' That is more than a school reassigns between picture day and retakes -- check the teacher column '
        + 'before importing. A teacher name that fits no one teacher also counts: it creates a second teacher.'
      : '';
    items.push({
      key: 'class-moves',
      severity: alert ? 'alert' : 'info',
      message: `This file moves ${plural(moved, 'child', 'children')} (${pct}% of those already in a class) to a `
        + `different teacher: ${shown}.${advice}`,
    });
  }

  const wide = classMoves.wide_classes || [];
  if (wide.length > 0) {
    const named = wide.slice(0, 5).map((w) => `"${w.teacher}" (${w.count}, grades ${w.grades.join(', ')})`).join('; ');
    const more = wide.length > 5 ? ` and ${wide.length - 5} more` : '';
    items.push({
      key: 'wide-classes',
      severity: 'warn',
      message: `${plural(wide.length, 'class spans', 'classes span')} five or more grades: ${named}${more}. `
        + 'Expected for homeschool, independent-study and some special-day classes; otherwise the teacher column '
        + 'may hold a staff member, a period or a placeholder instead of the teacher.',
    });
  }

  return items;
};

// StudentImportService::TeacherNames: teacher names written differently from the
// teacher's record ("Bledsoe" for "Bledsoe, Seth"), which the import now puts on
// that teacher, and the names it cannot tie to one teacher, which the school has
// to answer. Elihu Beard, 10/20/2026: a surname-only retake file split 17 classes.
export const teacherNameFindings = (teacherNames, studentImportId) => {
  if (!teacherNames) return [];
  if (teacherNames.error) return [{ key: 'teacher-names-error', severity: 'warn', message: teacherNames.error }];

  const items = [];
  const matched = teacherNames.matched || [];
  if (matched.length > 0) {
    const shown = matched.map((m) => `"${m.teacher}" → ${m.matched} (${m.count})`).join('; ');
    items.push({
      key: 'teacher-names-matched',
      severity: 'info',
      message: `${plural(matched.length, 'teacher name is', 'teacher names are')} written differently from the `
        + `teacher's record and will be imported as that teacher: ${shown}.`,
    });
  }

  const questions = teacherNames.questions || [];
  if (questions.length > 0) {
    const children = questions.reduce((sum, q) => sum + q.count, 0);
    const shown = questions.map((q) => {
      const options = q.candidates.length > 0 ? `: ${q.candidates.join(' or ')}` : '';
      return `"${q.teacher}" ${q.question}${options} (${q.count})`;
    }).join('; ');
    items.push({
      key: 'teacher-names-questions',
      severity: 'alert',
      message: `Ask the school: ${plural(questions.length, 'teacher name', 'teacher names')} for `
        + `${plural(children, 'child', 'children')} could not be tied to one teacher: ${shown}. If imported `
        + 'as is, each becomes a new teacher of that name.',
      link: studentImportId
        ? {
          href: `/admin/student_imports/${studentImportId}/teacher_questions.csv`,
          label: 'Download the children (CSV)',
        }
        : null,
    });
  }

  return items;
};

export const buildTeacherFindings = ({ rowPreview, pairedReview, studentImportId = null }) => {
  const items = [];

  if (rowPreview && rowPreview.available) {
    const newNames = rowPreview.new_educator_names;
    if (newNames && newNames.length > 0) {
      items.push({
        key: 'new-educators',
        severity: 'new',
        message: `${newNames.length} teacher name(s) don't match an existing teacher and will create a new one: `
          + `${newNames.join(', ')}.`,
      });
    }

    items.push(...teacherNameFindings(rowPreview.teacher_names, studentImportId));
    items.push(...classMoveFindings(rowPreview.class_moves));
  }

  if (pairedReview && pairedReview.available) {
    const gaps = pairedReview.cross_check && pairedReview.cross_check.teachers_missing_from_staff_file;
    if (gaps && gaps.length > 0) {
      items.push({
        key: 'staff-gaps',
        severity: 'warn',
        message: `${gaps.length} teacher(s) named in the roster have no match in the staff file, so they won't `
          + `get a staff photo record from it: ${gaps.join(', ')}.`,
      });
    }

    const { anomalies } = pairedReview;
    if (anomalies) {
      // A failed LLM call must say so, not silently look identical to
      // "checked, nothing found" -- ok is explicitly false only on error.
      if (anomalies.ok === false) {
        items.push({
          key: 'anomaly-error',
          severity: 'warn',
          message: `Could not run the placeholder-name check (${anomalies.error}) -- review the staff/teacher `
            + 'names yourself before continuing.',
        });
      } else if (anomalies.flagged && anomalies.flagged.length > 0) {
        const named = anomalies.flagged.map((flag) => `${flag.name} (${flag.reason})`).join(', ');
        items.push({
          key: 'anomaly-flags',
          severity: 'warn',
          message: 'These staff/teacher names look like placeholders rather than real people -- double-check '
            + `them before continuing: ${named}.`,
        });
      }
    }
  }

  return items;
};
